import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent } from "../../../components/ui/card";
import { Checkbox } from "../../../components/ui/checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";
import { ArrowLeft, Building2, MapPin, Plus, Building, UserRound, FileText, Edit3, Activity, Scale, HardHat } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Date as DateInput } from '../../../components/ui/date';
import {Notification as customNotification } from '../../../components/ui/custom-notification';
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from '../../../components/ui/select';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import AddNewItemDialog from '../../../components/ui/AddNewItemDialog';
import { UncheckWarningDialog } from '../../../components/ui/UncheckWarningDialog';
import { Project } from '../../project/types/project';
import { AddUserCategory, CreateProject, getListUserCategory, getAllContractors, createUpdateContractor } from '../../../redux/Project/saga';
import { fetchProjectList } from '../../../redux/Project/action';
import { STATUS_COLORS, STATUS_TYPES } from '../constants/filterConstants';
import { StatusBadge } from '../../project/components/dashboard/overview/components/common';
import { 
  ProjectDataAPI, 
  createDefaultFormData,
  createDefaultFeaturesConfig,
  createDefaultProjectTypeConfig,
  createDefaultMachineConfig,
  mergePipelineCoatingFromApi,
} from '../../../types/project';
import { AntdDate } from '../../../components/ui/antd-date';
import dayjs from 'dayjs';
import pipefitterApi from '../../../services/pipefitterApi';

type CheckboxHandler = (checked: CheckedState) => void;

const CRCE_WELDING_MACHINE_KEYS = ['IWM', 'P625', 'P600Z', 'M500', 'Manual'] as const;
type CrceWeldingMachineKey = typeof CRCE_WELDING_MACHINE_KEYS[number];

const CRCE_WELDING_MACHINE_OPTIONS = [
  { key: 'IWM', display: 'IWM' },
  { key: 'P625', display: 'P-625' },
  { key: 'P600Z', display: 'P-600Z' },
  { key: 'M500', display: 'M-500' },
  { key: 'Manual', display: 'Manual' },
] as const;

const isAnyCrceWeldingMachineEnabled = (
  machines?: Record<string, { enabled?: boolean } | undefined>
) => CRCE_WELDING_MACHINE_KEYS.some((key) => machines?.[key]?.enabled);

// Utility function to format date for HTML date input (YYYY-MM-DD)
const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // Get local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD format in local timezone
  } catch (error) {
    return '';
  }
};

interface ProjectCreationFormProps {
  onBack?: () => void;
  onEditSubmit?: () => void;
  isEditProject?: boolean;
  projectData?: Project;
}

const ProjectCreationForm: React.FC<ProjectCreationFormProps> = ({ onBack, onEditSubmit, projectData, isEditProject }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<ProjectDataAPI>(() => {
    if (isEditProject && projectData) {
      // Create default form data first
      const defaultData = createDefaultFormData();
      // Merge with project data, ensuring proper structure for non-CRCE machines
      return {
        ...defaultData,
        ...projectData,
        projectName: String(projectData.projectName || ''),
        projectNumber: String(projectData.projectNumber || ''),
        location: String(projectData.location || ''),
        customer: String(projectData.customer || ''),
        contractorName: String(
          projectData.contractorName || projectData.contractor_name || ''
        ),
        unit: String(projectData.unit || 'in'),
        startDate: formatDateForInput(projectData.startDate || ''),
        endDate: formatDateForInput(projectData.endDate || ''),
        progress: String(projectData.progress || STATUS_TYPES[0]),
        projectTypes: {
          ...defaultData.projectTypes,
          ...projectData.projectTypes,
          // Ensure proper structure for each project type
          pipeline: {
            ...defaultData.projectTypes.pipeline,
            ...projectData.projectTypes?.pipeline,
            isDocumentAvailable: projectData.projectTypes?.pipeline?.isDocumentAvailable || false,
            isPipeAvailable: projectData.projectTypes?.pipeline?.isPipeAvailable || false,
            features: {
              ...defaultData.projectTypes.pipeline.features,
              ...projectData.projectTypes?.pipeline?.features,
              welding: {
                ...defaultData.projectTypes.pipeline.features.welding,
                ...projectData.projectTypes?.pipeline?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.pipeline.features.welding.machines,
                  ...projectData.projectTypes?.pipeline?.features?.welding?.machines,
                }
              },
              coating: mergePipelineCoatingFromApi(
                defaultData.projectTypes.pipeline.features.coating,
                projectData.projectTypes?.pipeline?.features?.coating
              ),
            }
          },
          fabrication: {
            ...defaultData.projectTypes.fabrication,
            ...projectData.projectTypes?.fabrication,
         
            features: {
              ...defaultData.projectTypes.fabrication.features,
              ...projectData.projectTypes?.fabrication?.features,
              welding: {
                ...defaultData.projectTypes.fabrication.features.welding,
                ...projectData.projectTypes?.fabrication?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.fabrication.features.welding.machines,
                  ...projectData.projectTypes?.fabrication?.features?.welding?.machines,
                }
              }
            },
            options: {
              ...defaultData.projectTypes.fabrication.options,
              ...projectData.projectTypes?.fabrication?.options,
              buckleArrestor: {
                ...defaultData.projectTypes.fabrication.options.buckleArrestor,
                ...projectData.projectTypes?.fabrication?.options?.buckleArrestor,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.buckleArrestor?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.buckleArrestor?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.buckleArrestor.features,
                  ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.buckleArrestor.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features?.welding?.machines,
                    }
                  }
                }
              },
              pipeInPipe: {
                ...defaultData.projectTypes.fabrication.options.pipeInPipe,
                ...projectData.projectTypes?.fabrication?.options?.pipeInPipe,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.pipeInPipe?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.pipeInPipe?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.pipeInPipe.features,
                  ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.pipeInPipe.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features?.welding?.machines,
                    }
                  }
                }
              },
              bulkhead: {
                ...defaultData.projectTypes.fabrication.options.bulkhead,
                ...projectData.projectTypes?.fabrication?.options?.bulkhead,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.bulkhead?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.bulkhead?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.bulkhead.features,
                  ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.bulkhead.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.bulkhead.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features?.welding?.machines,
                    }
                  }
                }
              },
              spools: {
                ...defaultData.projectTypes.fabrication.options.spools,
                ...projectData.projectTypes?.fabrication?.options?.spools,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.spools?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.spools?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.spools.features,
                  ...projectData.projectTypes?.fabrication?.options?.spools?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.spools.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.spools?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.spools.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.spools?.features?.welding?.machines,
                    }
                  }
                }
              }
            }
          },
          others: {
            ...defaultData.projectTypes.others,
            ...projectData.projectTypes?.others,
            features: {
              ...defaultData.projectTypes.others.features,
              ...projectData.projectTypes?.others?.features,
              welding: {
                ...defaultData.projectTypes.others.features.welding,
                ...projectData.projectTypes?.others?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.others.features.welding.machines,
                  ...projectData.projectTypes?.others?.features?.welding?.machines,
                }
              }
            }
          }
        }
      };
    }
    return createDefaultFormData();
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const username = user && user['custom:first_name'] && user['custom:last_name'] ? user['custom:first_name'] + " " + user['custom:last_name'] :
  (user && user['custom:first_name'] ? user['custom:first_name'] : '')

  // State for dropdowns
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [isAddingContractor, setIsAddingContractor] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true); // Start with true for edit mode
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  // Units: display full names in dropdown, save short forms
  const UNIT_OPTIONS: { value: string; label: string }[] = [
    { value: "in", label: "Inch" },
    { value: "cm", label: "Centimeter" },
    { value: "mm", label: "Millimeter" },
    { value: "ft", label: "Foot" },
    { value: "m", label: "Meter" },
  ];
  // State for uncheck warning dialog
  const [showUncheckWarning, setShowUncheckWarning] = useState(false);
  const [uncheckWarningData, setUncheckWarningData] = useState<{
    title: string;
    message: string;
    hasDocuments: boolean;
    hasPipes: boolean;
    onConfirm: () => void;
  } | null>(null);


  // Update form data when projectData changes (for edit mode)
  useEffect(() => {
    if (isEditProject && projectData) {
      // Create default form data first
      const defaultData = createDefaultFormData();
      
      setFormData(prev => ({
        ...prev,
        ...projectData,
        projectName: String(projectData.projectName || ''),
        projectNumber: String(projectData.projectNumber || ''),
        location: String(projectData.location || ''),
        customer: String(projectData.customer || ''),
        contractorName: String(
          projectData.contractorName || projectData.contractor_name || ''
        ),
        unit: String(projectData.unit || 'in'),
        startDate: formatDateForInput(projectData.startDate || ''),
        endDate: formatDateForInput(projectData.endDate || ''),
        projectTypes: {
          ...defaultData.projectTypes,
          ...projectData.projectTypes,
          // Ensure proper structure for each project type
          pipeline: {
            ...defaultData.projectTypes.pipeline,
            ...projectData.projectTypes?.pipeline,
            features: {
              ...defaultData.projectTypes.pipeline.features,
              ...projectData.projectTypes?.pipeline?.features,
              welding: {
                ...defaultData.projectTypes.pipeline.features.welding,
                ...projectData.projectTypes?.pipeline?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.pipeline.features.welding.machines,
                  ...projectData.projectTypes?.pipeline?.features?.welding?.machines,
                }
              },
              coating: mergePipelineCoatingFromApi(
                defaultData.projectTypes.pipeline.features.coating,
                projectData.projectTypes?.pipeline?.features?.coating
              ),
            }
          },
          fabrication: {
            ...defaultData.projectTypes.fabrication,
            ...projectData.projectTypes?.fabrication,
            features: {
              ...defaultData.projectTypes.fabrication.features,
              ...projectData.projectTypes?.fabrication?.features,
              welding: {
                ...defaultData.projectTypes.fabrication.features.welding,
                ...projectData.projectTypes?.fabrication?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.fabrication.features.welding.machines,
                  ...projectData.projectTypes?.fabrication?.features?.welding?.machines,
                }
              }
            },
            options: {
              ...defaultData.projectTypes.fabrication.options,
              ...projectData.projectTypes?.fabrication?.options,
              buckleArrestor: {
                ...defaultData.projectTypes.fabrication.options.buckleArrestor,
                ...projectData.projectTypes?.fabrication?.options?.buckleArrestor,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.buckleArrestor?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.buckleArrestor?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.buckleArrestor.features,
                  ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.buckleArrestor.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.buckleArrestor?.features?.welding?.machines,
                    }
                  }
                }
              },
              pipeInPipe: {
                ...defaultData.projectTypes.fabrication.options.pipeInPipe,
                ...projectData.projectTypes?.fabrication?.options?.pipeInPipe,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.pipeInPipe?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.pipeInPipe?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.pipeInPipe.features,
                  ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.pipeInPipe.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.pipeInPipe?.features?.welding?.machines,
                    }
                  }
                }
              },
              bulkhead: {
                ...defaultData.projectTypes.fabrication.options.bulkhead,
                ...projectData.projectTypes?.fabrication?.options?.bulkhead,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.bulkhead?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.bulkhead?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.bulkhead.features,
                  ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.bulkhead.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.bulkhead.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.bulkhead?.features?.welding?.machines,
                    }
                  }
                }
              },
              spools: {
                ...defaultData.projectTypes.fabrication.options.spools,
                ...projectData.projectTypes?.fabrication?.options?.spools,
                isDocumentAvailable: projectData.projectTypes?.fabrication?.options?.spools?.isDocumentAvailable || false,
                isPipeAvailable: projectData.projectTypes?.fabrication?.options?.spools?.isPipeAvailable || false,
                features: {
                  ...defaultData.projectTypes.fabrication.options.spools.features,
                  ...projectData.projectTypes?.fabrication?.options?.spools?.features,
                  welding: {
                    ...defaultData.projectTypes.fabrication.options.spools.features.welding,
                    ...projectData.projectTypes?.fabrication?.options?.spools?.features?.welding,
                    machines: {
                      ...defaultData.projectTypes.fabrication.options.spools.features.welding.machines,
                      ...projectData.projectTypes?.fabrication?.options?.spools?.features?.welding?.machines,
                    }
                  }
                }
              }
            }
          },
          others: {
            ...defaultData.projectTypes.others,
            ...projectData.projectTypes?.others,
            features: {
              ...defaultData.projectTypes.others.features,
              ...projectData.projectTypes?.others?.features,
              welding: {
                ...defaultData.projectTypes.others.features.welding,
                ...projectData.projectTypes?.others?.features?.welding,
                machines: {
                  ...defaultData.projectTypes.others.features.welding.machines,
                  ...projectData.projectTypes?.others?.features?.welding?.machines,
                }
              }
            }
          }
        }
      }));
    }
  }, [isEditProject, projectData]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoadingData(true);
        // Load departments and companies in parallel
        const [companies, locations, contractorList] = await Promise.all([
          getListUserCategory('company'),
          getListUserCategory('location'),
          getAllContractors(),
        ]);

        
        // Remove duplicates and sort companies alphabetically by company_name
        const uniqueCompanies = companies
          .filter((company: any, index: number, self: any[]) => 
            index === self.findIndex((c: any) => c.company_name === company.company_name)
          )
          .sort((a: any, b: any) => a.company_name.localeCompare(b.company_name));
        
        // Remove duplicates and sort locations alphabetically by location_name
        const uniqueLocations = locations
          .filter((location: any, index: number, self: any[]) => 
            index === self.findIndex((l: any) => l.location_name === location.location_name)
          )
          .sort((a: any, b: any) => a.location_name.localeCompare(b.location_name))
          .map((location: any) => ({
            ...location,
            location_name: location.location_name.toUpperCase()
          }));

        const uniqueContractors = (contractorList || [])
          .filter((c: any, index: number, self: any[]) =>
            index === self.findIndex((x: any) =>
              String(x.contractor_name || '').toLowerCase() === String(c.contractor_name || '').toLowerCase()
            )
          )
          .sort((a: any, b: any) =>
            String(a.contractor_name || '').localeCompare(String(b.contractor_name || ''), undefined, { sensitivity: 'base' })
          );
        
        setCustomers(uniqueCompanies);
        setContractors(uniqueContractors);
        setLocations(uniqueLocations);
        
        // Ensure loading state is set to false after all data is loaded  
        setIsLoadingData(false);

      } catch (error) {
        // Set empty arrays as fallback
        setCustomers([]);
        setContractors([]);
        setLocations([]);
        setIsLoadingData(false);
      }
    };
    loadInitialData();
  }, []);

  // Handler functions for dropdowns

  const handleAddLocation = async (locationName: string) => {

      if(locationName.trim()){
        if(locations.find((location: any) => location.location_name === locationName.trim())){
          customNotification.warning(`${locationName.trim()} already exists`,'please add a new location');
          return;
        }
      }
      // Add new location logic here
    const newLocationData = {
      location_name: locationName.toUpperCase(),
    };
    const res = await AddUserCategory(newLocationData, 'location');
    if(res){
      setLocations(prev => [...prev, res]);
    setFormData(prev => ({ ...prev, location: res.location_name }));

    }
  
    // Refresh locations data after successful addition
    //const updatedLocations = await getListUserCategory('location');
    //setLocations(updatedLocations);
    // Find the newly added location and set its ID
    // You can add API call here to save the new location
  };

  const handleAddCustomer = async (customerName: string) => { 
    // Add new customer logic here
    if(customerName.trim()){
      if(customers.find((customer: any) => customer.company_name === customerName.trim())){
        customNotification.warning(`${customerName.trim()} already exists`,'please add a new customer');
        return;
      }
    }
    const newCustomer = { company_name: customerName };
  
    const res = await AddUserCategory(newCustomer, 'company');
    if(res){
      setCustomers(prev => [...prev, res]);
      setFormData(prev => ({ ...prev, customer: res.company_name }));
    }
    // You can add API call here to save the new customer
  };

  const handleAddContractor = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (
      contractors.find(
        (c: any) => String(c.contractor_name || '').toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      customNotification.warning(`${trimmed} already exists`, 'Please add a new contractor');
      return;
    }
    const res = await createUpdateContractor({ contractor_name: trimmed });
    if (res) {
      const row = {
        contractor_id: res.contractor_id ?? res.contractorId,
        contractor_name: res.contractor_name ?? res.contractorName ?? trimmed,
      };
      setContractors((prev) =>
        [...prev, row].sort((a: any, b: any) =>
          String(a.contractor_name || '').localeCompare(String(b.contractor_name || ''), undefined, {
            sensitivity: 'base',
          })
        )
      );
      setFormData((prev) => ({ ...prev, contractorName: row.contractor_name }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if(name === 'projectName' || name === 'projectNumber'){
      setFormData(prev => ({ ...prev, [name]: value.trimStart() }));
    }else{
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Real-time date validation
    if (name === 'startDate' && value && formData.endDate) {
      const startDate = new Date(value);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        customNotification.warning('Start date cannot be in the past');
      } else if (endDate <= startDate) {
        customNotification.warning('End date must be after start date');
      }
    }

    if (name === 'endDate' && value && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(value);
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);

      if (endDate <= startDate) {
        customNotification.warning('End date must be after start date');
      } else if (endDate < nextDay) {
        customNotification.warning('End date must be at least the next day after start date');
      }
    }
  };

  // Helper functions to check if pipes or documents are available
  const checkIfPipesOrDocumentsAvailable = (projectType: string, option?: string) => {
    if (projectType === 'pipeline') {
      return {
        hasPipes: formData.projectTypes?.pipeline?.isPipeAvailable || false,
        hasDocuments: formData.projectTypes?.pipeline?.isDocumentAvailable || false
      };
    }
    else if (projectType === 'fabrication' && option) {
      // Check specific fabrication option
      const fabricationOption = formData.projectTypes?.fabrication?.options?.[option as keyof typeof formData.projectTypes.fabrication.options];
      return {
        hasPipes: fabricationOption?.isPipeAvailable || false,
        hasDocuments: fabricationOption?.isDocumentAvailable || false
      };
    }
    else if (projectType === 'fabrication') {
      // Check if ANY fabrication option has pipes or documents
      const buckleArrestor = formData.projectTypes?.fabrication?.options?.buckleArrestor;
      const pipeInPipe = formData.projectTypes?.fabrication?.options?.pipeInPipe;
      const bulkhead = formData.projectTypes?.fabrication?.options?.bulkhead;
      const spools = formData.projectTypes?.fabrication?.options?.spools;
      
      const hasPipes = (buckleArrestor?.isPipeAvailable || false) || 
                      (pipeInPipe?.isPipeAvailable || false) || 
                      (bulkhead?.isPipeAvailable || false) ||
                      (spools?.isPipeAvailable || false);
      
      const hasDocuments = (buckleArrestor?.isDocumentAvailable || false) || 
                          (pipeInPipe?.isDocumentAvailable || false) || 
                          (bulkhead?.isDocumentAvailable || false) ||
                          (spools?.isDocumentAvailable || false);
      
      return { hasPipes, hasDocuments };
    }
    else if (projectType === 'others') {
      return {
        hasPipes: formData.projectTypes?.others?.isPipeAvailable || false,
        hasDocuments: formData.projectTypes?.others?.isDocumentAvailable || false
      };
    }
    return { hasPipes: false, hasDocuments: false };
  };

  const showUncheckWarningDialog = (
    title: string,
    message: string,
    hasDocuments: boolean,
    hasPipes: boolean,
    onConfirm: () => void
  ) => {
    setUncheckWarningData({
      title,
      message,
      hasDocuments,
      hasPipes,
      onConfirm
    });
    setShowUncheckWarning(true);
  };

  const handleUncheckWarningConfirm = () => {
    if (uncheckWarningData?.onConfirm) {
      uncheckWarningData.onConfirm();
    }
    setShowUncheckWarning(false);
    setUncheckWarningData(null);
  };

  const handleUncheckWarningCancel = () => {
    setShowUncheckWarning(false);
    setUncheckWarningData(null);
  };

  const handleCheckboxChange = (section: 'projectTypes', key: string): CheckboxHandler => {
    return (checked: CheckedState) => {
      // Check if trying to uncheck and if pipes/documents are available
      if (checked === false) {
        const { hasPipes, hasDocuments } = checkIfPipesOrDocumentsAvailable(key);
        
        if (hasPipes || hasDocuments) {
          const title = `Cannot Uncheck ${key.charAt(0).toUpperCase() + key.slice(1)}`;
          const message = `Cannot uncheck ${key} because it has associated pipes or documents. Please remove all pipes and documents before unchecking this option.`;
          
          showUncheckWarningDialog(title, message, hasDocuments, hasPipes, () => {
            // This will be called if user confirms to continue anyway
            // For now, we'll prevent the uncheck
            onBack?.();
          });
          return; // Don't proceed with unchecking
        }
      }

      setFormData(prev => {
        const newData = {
          ...prev,
          projectTypes: {
            ...prev.projectTypes,
            [key]: {
              ...prev.projectTypes?.[key as keyof typeof prev.projectTypes],
              enabled: checked,
            },
          },
        };

        if (section === 'projectTypes') {
          if (!newData.projectTypes?.fabrication?.enabled) {
            newData.projectTypes = {
              ...newData.projectTypes,
              fabrication: {
                enabled: false,
                features: createDefaultFeaturesConfig(),
                options: {
                  buckleArrestor: {
                    selected: false,
                    features: {
                      materialTraceability: { enabled: false, machines: {} },
                      pipeFitter: { enabled: false, machines: {} },
                      welding: { 
                        enabled: false, 
                        machines: {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                      },
                      ndt: { enabled: false, machines: {} },
                      hydro: { enabled: false, machines: {} },
                      torquing: { enabled: false, machines: {} },
                      coating: { enabled: false, machines: {} },
                    },
                  },
                  pipeInPipe: {
                    selected: false,
                    features: {
                      materialTraceability: { enabled: false, machines: {} },
                      pipeFitter: { enabled: false, machines: {} },
                      welding: { 
                        enabled: false, 
                        machines: {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                      },
                      ndt: { enabled: false, machines: {} },
                      hydro: { enabled: false, machines: {} },
                      torquing: { enabled: false, machines: {} },
                      coating: { enabled: false, machines: {} },
                    },
                  },
                  bulkhead: {
                    selected: false,
                    features: {
                      materialTraceability: { enabled: false, machines: {} },
                      pipeFitter: { enabled: false, machines: {} },
                      welding: { 
                        enabled: false, 
                        machines: {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                      },
                      ndt: { enabled: false, machines: {} },
                      hydro: { enabled: false, machines: {} },
                      torquing: { enabled: false, machines: {} },
                      coating: { enabled: false, machines: {} },
                    },
                  },
                  spools: {
                    selected: false,
                    features: {
                      materialTraceability: { enabled: false, machines: {} },
                      pipeFitter: { enabled: false, machines: {} },
                      welding: { 
                        enabled: false, 
                        machines: {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                      },
                      ndt: { enabled: false, machines: {} },
                      hydro: { enabled: false, machines: {} },
                      torquing: { enabled: false, machines: {} },
                      coating: { enabled: false, machines: {} },
                    },
                  },
                },
              },
            };
          }
          if (!newData.projectTypes?.pipeline?.enabled) {
            newData.projectTypes = {
              ...newData.projectTypes,
              pipeline: createDefaultProjectTypeConfig(),
            };
          }
          if (!newData.projectTypes?.others?.enabled) {
            newData.projectTypes = {
              ...newData.projectTypes,
              others: createDefaultProjectTypeConfig(),
            };
          }
        }

        return newData;
      });
    };
  };

  const handleFabricationOptionChange = (option: 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools'): CheckboxHandler => {
    return (checked: CheckedState) => {
      // Check if trying to uncheck and if pipes/documents are available
      if (checked === false) {
        const { hasPipes, hasDocuments } = checkIfPipesOrDocumentsAvailable('fabrication', option);
        
        if (hasPipes || hasDocuments) {
          const title = `Cannot Uncheck ${option.charAt(0).toUpperCase() + option.slice(1)}`;
          const message = `Cannot uncheck ${option} because it has associated pipes or documents. Please remove all pipes and documents before unchecking this option.`;
          
          showUncheckWarningDialog(title, message, hasDocuments, hasPipes, () => {
            // This will be called if user confirms to continue anyway
            // For now, we'll prevent the uncheck
           // customNotification.warning('Please remove pipes and documents first before unchecking this option.');
           onBack?.();
          });
          return; // Don't proceed with unchecking
        }
      }

      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          fabrication: {
            ...prev.projectTypes?.fabrication,
            options: {
              ...prev.projectTypes?.fabrication?.options,
              [option]: {
                ...prev.projectTypes?.fabrication?.options?.[option],
                selected: checked === true,
                features: checked === true
                  ? prev.projectTypes?.fabrication?.options?.[option]?.features
                  : {
                      materialTraceability: { enabled: false, machines: {} },
                      pipeFitter: { enabled: false, machines: {} },
                      welding: { 
                        enabled: false, 
                        machines: {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                      },
                      ndt: { enabled: false, machines: {} },
                      hydro: { enabled: false, machines: {} },
                      torquing: { enabled: false, machines: {} },
                      coating: { enabled: false, machines: {} },
                    },
              },
            },
          },
        },
      }));
    };
  };

  const handleWeldingFeatureChange = (fabricationType: 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools'): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          fabrication: {
            ...prev.projectTypes?.fabrication,
            options: {
              ...prev.projectTypes?.fabrication?.options,
              [fabricationType]: {
                ...prev.projectTypes?.fabrication?.options?.[fabricationType],
                features: {
                  ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features,
                  welding: {
                    ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features?.welding,
                    enabled: checked === true,
                    machines: checked === true
                      ? prev.projectTypes?.fabrication?.options?.[fabricationType]?.features?.welding?.machines || {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        }
                      : {
                          IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                          P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                          M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                        },
                  },
                },
              },
            },
          },
        },
      }));
    };
  };

  const handleWeldingMachineChange = (
    fabricationType: 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools',
    machineType: 'CRCE' | 'NonCRCE',
    machine?: 'IWM' | 'P625' | 'P600Z' | 'M500' | 'Manual' | 'competitorMachine'
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          fabrication: {
            ...prev.projectTypes?.fabrication,
            options: {
              ...prev.projectTypes?.fabrication?.options,
              [fabricationType]: {
                ...prev.projectTypes?.fabrication?.options?.[fabricationType],
                features: {
                  ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features,
                  welding: {
                    ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features?.welding,
                    machines: {
                      ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features?.welding?.machines,
                      ...(machineType === 'CRCE' && machine && CRCE_WELDING_MACHINE_KEYS.includes(machine as CrceWeldingMachineKey)
                        ? {
                          [machine]: { 
                            enabled: checked === true, 
                            category: "CRCE", 
                            feature: "WELDING" 
                          },
                        }
                        : machineType === 'NonCRCE' && machine === 'competitorMachine'
                        ? {
                          [machine]: { 
                            enabled: checked === true, 
                            category: "NonCRCE", 
                            feature: "WELDING",
                            nonCRCMachines: {
                              enabled: false,
                              machines: {}
                            }
                          },
                        }
                        : {}),
                    },
                  },
                },
              },
            },
          },
        },
      }));
    };
  };

  const handleFeatureChange = (
    fabricationType: 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools',
    feature: 'materialTraceability' | 'pipeFitter' | 'ndt' | 'hydro' | 'torquing' | 'coating'
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          fabrication: {
            ...prev.projectTypes?.fabrication,
            options: {
              ...prev.projectTypes?.fabrication?.options,
              [fabricationType]: {
                ...prev.projectTypes?.fabrication?.options?.[fabricationType],
                features: {
                  ...prev.projectTypes?.fabrication?.options?.[fabricationType]?.features,
                  [feature]: { enabled: checked === true, machines: {} },
                },
              },
            },
          },
        },
      }));
    };
  };

  const handlePipelineFeatureChange = (feature: 'materialTraceability' | 'pipeFitter' | 'ndt' | 'coating'): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          pipeline: {
            ...prev.projectTypes?.pipeline,
            features: {
              ...prev.projectTypes?.pipeline?.features,
              ...(feature === 'coating'
                ? {
                    coating: {
                      enabled: checked === true,
                      machines: checked === true
                        ? {
                            ...(prev.projectTypes?.pipeline?.features?.coating?.machines || {}),
                            pumpHouse: {
                              ...(prev.projectTypes?.pipeline?.features?.coating?.machines?.pumpHouse ||
                                createDefaultMachineConfig('pumpHouse', 'Coating')),
                              enabled:
                                prev.projectTypes?.pipeline?.features?.coating?.machines?.pumpHouse
                                  ?.enabled ?? false,
                            },
                          }
                        : {
                            pumpHouse: createDefaultMachineConfig('pumpHouse', 'Coating'),
                          },
                    },
                  }
                : { [feature]: { enabled: checked === true, machines: {} } }),
            },
          },
        },
      }));
    };
  };

  const handlePipelineCoatingPumpHouseChange: CheckboxHandler = (checked: CheckedState) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: {
        ...prev.projectTypes,
        pipeline: {
          ...prev.projectTypes?.pipeline,
          features: {
            ...prev.projectTypes?.pipeline?.features,
            coating: {
              ...prev.projectTypes?.pipeline?.features?.coating,
              enabled: prev.projectTypes?.pipeline?.features?.coating?.enabled ?? false,
              machines: {
                ...prev.projectTypes?.pipeline?.features?.coating?.machines,
                pumpHouse: {
                  ...(prev.projectTypes?.pipeline?.features?.coating?.machines?.pumpHouse ||
                    createDefaultMachineConfig('pumpHouse', 'Coating')),
                  enabled: checked === true,
                  category: 'pumpHouse',
                  feature: 'Coating',
                },
              },
            },
          },
        },
      },
    }));
  };

  const handlePipelineWeldingFeatureChange: CheckboxHandler = (checked: CheckedState) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: {
        ...prev.projectTypes,
        pipeline: {
          ...prev.projectTypes?.pipeline,
          features: {
            ...prev.projectTypes?.pipeline?.features,
            welding: {
              enabled: checked === true,
              machines: checked === true
                ? prev.projectTypes?.pipeline?.features?.welding?.machines || {
                    IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                    M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                  }
                : {
                    IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                    M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                  },
            },
          },
        },
      },
    }));
  };

  const handlePipelineWeldingMachineChange = (
    machineType: 'CRCE' | 'NonCRCE',
    machine?: 'IWM' | 'P625' | 'P600Z' | 'M500' | 'Manual' | 'competitorMachine'
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          pipeline: {
            ...prev.projectTypes?.pipeline,
            features: {
              ...prev.projectTypes?.pipeline?.features,
              welding: {
                ...prev.projectTypes?.pipeline?.features?.welding,
                machines: {
                  ...prev.projectTypes?.pipeline?.features?.welding?.machines,
                  ...(machineType === 'CRCE' && machine && CRCE_WELDING_MACHINE_KEYS.includes(machine as CrceWeldingMachineKey)
                    ? {
                      [machine]: { 
                        enabled: checked === true, 
                        category: "CRCE", 
                        feature: "WELDING" 
                      },
                    }
                    : machineType === 'NonCRCE' && machine === 'competitorMachine'
                    ? {
                      [machine]: { 
                        enabled: checked === true, 
                        category: "NonCRCE", 
                        feature: "WELDING",
                        nonCRCMachines: {
                          enabled: false,
                          machines: {}
                        }
                      },
                    }
                    : {}),
                },
              },
            },
          },
        },
      }));
    };
  };

  const handleOthersFeatureChange = (feature: 'materialTraceability' | 'pipeFitter' | 'ndt' | 'coating'): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          others: {
            ...prev.projectTypes?.others,
            features: {
              ...prev.projectTypes?.others?.features,
              [feature]: { enabled: checked === true, machines: {} },
            },
          },
        },
      }));
    };
  };

  const handleOthersWeldingFeatureChange: CheckboxHandler = (checked: CheckedState) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: {
        ...prev.projectTypes,
        others: {
          ...prev.projectTypes?.others,
          features: {
            ...prev.projectTypes?.others?.features,
            welding: {
              enabled: checked === true,
              machines: checked === true
                ? prev.projectTypes?.others?.features?.welding?.machines || {
                    IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                    M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                  }
                : {
                    IWM: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P625: { enabled: false, category: "CRCE", feature: "WELDING" },
                    P600Z: { enabled: false, category: "CRCE", feature: "WELDING" },
                    M500: { enabled: false, category: "CRCE", feature: "WELDING" },
                  },
            },
          },
        },
      },
    }));
  };

  const handleOthersWeldingMachineChange = (
    machineType: 'CRCE' | 'NonCRCE',
    machine?: 'IWM' | 'P625' | 'P600Z' | 'M500' | 'Manual' | 'competitorMachine'
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        projectTypes: {
          ...prev.projectTypes,
          others: {
            ...prev.projectTypes?.others,
            features: {
              ...prev.projectTypes?.others?.features,
              welding: {
                ...prev.projectTypes?.others?.features?.welding,
                machines: {
                  ...prev.projectTypes?.others?.features?.welding?.machines,
                  ...(machineType === 'CRCE' && machine && CRCE_WELDING_MACHINE_KEYS.includes(machine as CrceWeldingMachineKey)
                    ? {
                      [machine]: { 
                        enabled: checked === true, 
                        category: "CRCE", 
                        feature: "WELDING" 
                      },
                    }
                    : machineType === 'NonCRCE' && machine === 'competitorMachine'
                    ? {
                      [machine]: { 
                        enabled: checked === true, 
                        category: "NonCRCE", 
                        feature: "WELDING",
                        nonCRCMachines: {
                          enabled: false,
                          machines: {}
                        }
                      },
                    }
                    : {}),
                },
              },
            },
          },
        },
      }));
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Helper function to safely check if a value is a non-empty string
    const isValidString = (value: any): boolean => {
      return typeof value === 'string' && value.trim().length > 0;
    };

    if (!isValidString(formData.projectName)) {
      customNotification.warning('Project name is required');
      return;
    }

    if (!isValidString(formData.projectNumber)) {
      customNotification.warning('Project number is required');
      return;
    }
    if (!isValidString(formData.location)) {
      customNotification.warning('Location is required');
      return;
    }
    if (!isValidString(formData.customer)) {
      customNotification.warning('Customer is required');
      return;
    }
    if (!isValidString(formData.unit)) {
      customNotification.warning('Unit is required');
      return;
    }
    if (!isValidString(formData.startDate)) {
      customNotification.warning('Start date is required');
      return;
    }

    // Date validation
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      // // Check if start date is in the past
      // if (startDate < today) {
      //   customNotification.warning('Start date cannot be in the past');
      //   return;
      // }

      // // Check if end date is before or equal to start date
      // if (endDate <= startDate) {
      //   customNotification.warning('End date must be after start date');
      //   return;
      // }

      // Check if end date is at least the next day after start date
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      if (endDate < nextDay) {
        customNotification.warning('End date must be at least the next day after start date');
        return;
      }
    }
    // Check if at least one project type is selected
    if (!formData.projectTypes?.pipeline?.enabled && !formData.projectTypes?.fabrication?.enabled && !formData.projectTypes?.others?.enabled) {
      customNotification.warning('At least one project type is required');
      return;
    }

    // Validate Pipeline project type
    if (formData.projectTypes?.pipeline?.enabled) {
      // Check if at least one pipeline feature is selected
      const hasPipelineFeatures = formData.projectTypes.pipeline.features?.materialTraceability?.enabled ||
        formData.projectTypes.pipeline.features?.pipeFitter?.enabled ||
        formData.projectTypes.pipeline.features?.welding?.enabled ||
        formData.projectTypes.pipeline.features?.ndt?.enabled ||
        formData.projectTypes.pipeline.features?.coating?.enabled;

      if (!hasPipelineFeatures) {
        customNotification.warning('At least one pipeline feature is required');
        return;
      }

      // Validate welding machines if welding is enabled
      if (formData.projectTypes.pipeline.features?.welding?.enabled) {
        const hasWeldingMachines = formData.projectTypes.pipeline.features.welding.machines?.IWM?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.P625?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.P600Z?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.M500?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.Manual?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.competitorMachine?.enabled;

        if (!hasWeldingMachines) {
          customNotification.warning('At least one welding machine is required when welding is enabled');
          return;
        }
      }
    }

    // Validate Fabrication project type
    if (formData.projectTypes?.fabrication?.enabled) {
      // Check if at least one fabrication option is selected
      const hasFabricationOptions = formData.projectTypes.fabrication.options?.buckleArrestor?.selected ||
        formData.projectTypes.fabrication.options?.pipeInPipe?.selected ||
        formData.projectTypes.fabrication.options?.bulkhead?.selected ||
        formData.projectTypes.fabrication.options?.spools?.selected;

      if (!hasFabricationOptions) {
        customNotification.warning('At least one fabrication option is required');
        return;
      }

      // Validate each selected fabrication option has at least one feature
      if (formData.projectTypes.fabrication.options?.buckleArrestor?.selected) {
        const hasBuckleArrestorFeatures = formData.projectTypes.fabrication.options.buckleArrestor.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.coating?.enabled;

        if (!hasBuckleArrestorFeatures) {
          customNotification.warning('At least one feature is required for Buckle Arrestor');
          return;
        }

        // Validate welding machines if welding is enabled for Buckle Arrestor
        if (formData.projectTypes.fabrication.options.buckleArrestor.features?.welding?.enabled) {
          const hasWeldingMachines = formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            customNotification.warning('At least one welding machine is required for Buckle Arrestor when welding is enabled');
            return;
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.pipeInPipe?.selected) {
        const hasPipeInPipeFeatures = formData.projectTypes.fabrication.options.pipeInPipe.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.coating?.enabled;

        if (!hasPipeInPipeFeatures) {
          customNotification.warning('At least one feature is required for Pipe in Pipe');
          return;
        }

        // Validate welding machines if welding is enabled for Pipe in Pipe
        if (formData.projectTypes.fabrication.options.pipeInPipe.features?.welding?.enabled) {
          const hasWeldingMachines = formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            customNotification.warning('At least one welding machine is required for Pipe in Pipe when welding is enabled');
            return;
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.bulkhead?.selected) {
        const hasBulkheadFeatures = formData.projectTypes.fabrication.options.bulkhead.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.coating?.enabled;

        if (!hasBulkheadFeatures) {
          customNotification.warning('At least one feature is required for Bulkhead');
          return;
        }

        // Validate welding machines if welding is enabled for Bulkhead
        if (formData.projectTypes.fabrication.options.bulkhead.features?.welding?.enabled) {
          const hasWeldingMachines = formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            customNotification.warning('At least one welding machine is required for Bulkhead when welding is enabled');
            return;
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.spools?.selected) {
        const hasSpoolTrackerFeatures = formData.projectTypes.fabrication.options.spools.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.coating?.enabled;

        if (!hasSpoolTrackerFeatures) {
          customNotification.warning('At least one feature is required for Spooltracker');
          return;
        }

        if (formData.projectTypes.fabrication.options.spools.features?.welding?.enabled) {
          const hasWeldingMachines = formData.projectTypes.fabrication.options.spools.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            customNotification.warning('At least one welding machine is required for Spooltracker when welding is enabled');
            return;
          }
        }
      }
    }

    // Validate Others project type
    if (formData.projectTypes?.others?.enabled) {
      // Check if at least one other feature is selected
      const hasOthersFeatures = formData.projectTypes.others.features?.materialTraceability?.enabled ||
        formData.projectTypes.others.features?.pipeFitter?.enabled ||
        formData.projectTypes.others.features?.welding?.enabled ||
        formData.projectTypes.others.features?.ndt?.enabled ||
        formData.projectTypes.others.features?.coating?.enabled;

      if (!hasOthersFeatures) {
        customNotification.warning('At least one feature is required when others project type is selected');
        return;
      }

      // Validate welding machines if welding is enabled for Others
      if (formData.projectTypes.others.features?.welding?.enabled) {
        const hasWeldingMachines = formData.projectTypes.others.features.welding.machines?.IWM?.enabled ||
          formData.projectTypes.others.features.welding.machines?.P625?.enabled ||
          formData.projectTypes.others.features.welding.machines?.P600Z?.enabled ||
          formData.projectTypes.others.features.welding.machines?.M500?.enabled ||
          formData.projectTypes.others.features.welding.machines?.Manual?.enabled ||
          formData.projectTypes.others.features.welding.machines?.competitorMachine?.enabled;

        if (!hasWeldingMachines) {
          customNotification.warning('At least one welding machine is required when welding is enabled');
          return;
        }
      }
    }




    const data = {
      "projectId": isEditProject ? projectData?.projectId : null  ,
      "projectName": formData.projectName,
      "projectNumber": formData.projectNumber,
      "location": formData.location,
      "customer": formData.customer,
      "contractorName": (formData.contractorName || '').trim(),
      "unit": formData.unit || "in",
      "startDate": formData.startDate,
      "endDate": formData.endDate,
      "createdBy": username || '',
      "createdFromApp": "Data-360-Platform",
      "progress": formData.progress,
       projectTypes: formData.projectTypes
    }
    setIsLoading(true);
    try {
      const result = await CreateProject(data,isEditProject);
      if (result) {
        // Fetch project data again after successful creation
        dispatch(fetchProjectList());

        console.log('result', result.data.project.project_id);
        // When creating (not editing) with Pipeline + PipeFitter checked, create project in Pipefitter backend
        if (
          formData.projectTypes?.pipeline?.enabled &&
          formData.projectTypes?.pipeline?.features?.pipeFitter?.enabled
        ) {
          try {
            const pipefitterPayload = {
              projectNumber: formData.projectNumber,
              projectName: formData.projectName,
              location: formData.location,
              contractNumber: formData.contractorName,
              customerName: formData.customer,
              startDate: formData.startDate || '',
              endDate: formData.endDate || null,
              projectId: result.data.project.project_id || result.projectId,
            };
            await pipefitterApi.post('/project/create-or-update-project', pipefitterPayload);
          } catch (pipefitterError: any) {
            const message =
              pipefitterError?.response?.data?.message ??
              pipefitterError?.message ??
              'Pipefitter project creation failed.';
            customNotification.warning('Pipefitter project', message);
          }
        }

        // Call appropriate callback based on whether it's edit or create
        if (isEditProject && onEditSubmit) {
          await onEditSubmit();
        } else {
          await onBack?.();
        }
      }
    } catch (error) {
      // notification.error({
      //   message: 'Failed to create project',
      //   description: 'Please try again later.'
      // });
    } finally {
      setIsLoading(false);
    }

  };


  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return '';
    }
  };

  // Function to get validation messages for disabled submit button
  const getValidationMessages = () => {
    const messages: string[] = [];

    // Check basic required fields
    if (!formData.projectName?.trim()) {
      messages.push("Project Name is required");
    }
    if (!formData.projectNumber?.trim()) {
      messages.push("Project Number is required");
    }
    if (!formData.location?.trim()) {
      messages.push("Location is required");
    }
    if (!formData.customer?.trim()) {
      messages.push("Customer is required");
    }
    if (!formData.unit?.trim()) {
      messages.push("Unit is required");
    }
    if (!formData.startDate) {
      messages.push("Start Date is required");
    }
    // if (!formData.endDate) {
    //   messages.push("End Date is required");
    // }

    // Check if at least one project type is selected
    const hasProjectType = 
      formData.projectTypes?.pipeline?.enabled ||
      formData.projectTypes?.fabrication?.enabled ||
      formData.projectTypes?.others?.enabled;

    if (!hasProjectType) {
      messages.push("At least one Project Type must be selected");
    }

    // Validate Pipeline project type
    if (formData.projectTypes?.pipeline?.enabled) {
      const hasPipelineFeatures = 
        formData.projectTypes.pipeline.features?.materialTraceability?.enabled ||
        formData.projectTypes.pipeline.features?.pipeFitter?.enabled ||
        formData.projectTypes.pipeline.features?.welding?.enabled ||
        formData.projectTypes.pipeline.features?.ndt?.enabled ||
        formData.projectTypes.pipeline.features?.coating?.enabled;

      if (!hasPipelineFeatures) {
        messages.push("Pipeline: At least one feature must be selected");
      }

      // Validate welding machines if welding is enabled
      if (formData.projectTypes.pipeline.features?.welding?.enabled) {
        const hasWeldingMachines = 
          formData.projectTypes.pipeline.features.welding.machines?.IWM?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.P625?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.P600Z?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.M500?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.Manual?.enabled ||
          formData.projectTypes.pipeline.features.welding.machines?.competitorMachine?.enabled;

        if (!hasWeldingMachines) {
          messages.push("Pipeline Welding: At least one welding machine must be selected");
        }
      }
    }

    // Validate Fabrication project type
    if (formData.projectTypes?.fabrication?.enabled) {
      const hasFabricationOptions = 
        formData.projectTypes.fabrication.options?.buckleArrestor?.selected ||
        formData.projectTypes.fabrication.options?.pipeInPipe?.selected ||
        formData.projectTypes.fabrication.options?.bulkhead?.selected ||
        formData.projectTypes.fabrication.options?.spools?.selected;

      if (!hasFabricationOptions) {
        messages.push("Fabrication: At least one option must be selected");
      }

      // Validate each selected fabrication option has at least one feature
      if (formData.projectTypes.fabrication.options?.buckleArrestor?.selected) {
        const hasBuckleArrestorFeatures = 
          formData.projectTypes.fabrication.options.buckleArrestor.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.buckleArrestor.features?.coating?.enabled;

        if (!hasBuckleArrestorFeatures) {
          messages.push("Buckle Arrestor: At least one feature must be selected");
        }

        // Validate welding machines if welding is enabled for Buckle Arrestor
        if (formData.projectTypes.fabrication.options.buckleArrestor.features?.welding?.enabled) {
          const hasWeldingMachines = 
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.buckleArrestor.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            messages.push("Buckle Arrestor Welding: At least one welding machine must be selected");
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.pipeInPipe?.selected) {
        const hasPipeInPipeFeatures = 
          formData.projectTypes.fabrication.options.pipeInPipe.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.pipeInPipe.features?.coating?.enabled;

        if (!hasPipeInPipeFeatures) {
          messages.push("Pipe in Pipe: At least one feature must be selected");
        }

        // Validate welding machines if welding is enabled for Pipe in Pipe
        if (formData.projectTypes.fabrication.options.pipeInPipe.features?.welding?.enabled) {
          const hasWeldingMachines = 
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.pipeInPipe.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            messages.push("Pipe in Pipe Welding: At least one welding machine must be selected");
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.bulkhead?.selected) {
        const hasBulkheadFeatures = 
          formData.projectTypes.fabrication.options.bulkhead.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.bulkhead.features?.coating?.enabled;

        if (!hasBulkheadFeatures) {
          messages.push("Bulkhead: At least one feature must be selected");
        }

        // Validate welding machines if welding is enabled for Bulkhead
        if (formData.projectTypes.fabrication.options.bulkhead.features?.welding?.enabled) {
          const hasWeldingMachines = 
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.bulkhead.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            messages.push("Bulkhead Welding: At least one welding machine must be selected");
          }
        }
      }

      if (formData.projectTypes.fabrication.options?.spools?.selected) {
        const hasSpoolTrackerFeatures = 
          formData.projectTypes.fabrication.options.spools.features?.materialTraceability?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.pipeFitter?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.welding?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.ndt?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.hydro?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.torquing?.enabled ||
          formData.projectTypes.fabrication.options.spools.features?.coating?.enabled;

        if (!hasSpoolTrackerFeatures) {
          messages.push("Spooltracker: At least one feature must be selected");
        }

        if (formData.projectTypes.fabrication.options.spools.features?.welding?.enabled) {
          const hasWeldingMachines = 
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.IWM?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.P625?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.P600Z?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.M500?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.Manual?.enabled ||
            formData.projectTypes.fabrication.options.spools.features.welding.machines?.competitorMachine?.enabled;

          if (!hasWeldingMachines) {
            messages.push("Spooltracker Welding: At least one welding machine must be selected");
          }
        }
      }
    }

    // Validate Others project type
    if (formData.projectTypes?.others?.enabled) {
      const hasOthersFeatures = 
        formData.projectTypes.others.features?.materialTraceability?.enabled ||
        formData.projectTypes.others.features?.pipeFitter?.enabled ||
        formData.projectTypes.others.features?.welding?.enabled ||
        formData.projectTypes.others.features?.ndt?.enabled ||
        formData.projectTypes.others.features?.coating?.enabled;

      if (!hasOthersFeatures) {
        messages.push("Others: At least one feature must be selected");
      }

      // Validate welding machines if welding is enabled for Others
      if (formData.projectTypes.others.features?.welding?.enabled) {
        const hasWeldingMachines = 
          formData.projectTypes.others.features.welding.machines?.IWM?.enabled ||
          formData.projectTypes.others.features.welding.machines?.P625?.enabled ||
          formData.projectTypes.others.features.welding.machines?.P600Z?.enabled ||
          formData.projectTypes.others.features.welding.machines?.M500?.enabled ||
          formData.projectTypes.others.features.welding.machines?.Manual?.enabled ||
          formData.projectTypes.others.features.welding.machines?.competitorMachine?.enabled;

        if (!hasWeldingMachines) {
          messages.push("Others Welding: At least one welding machine must be selected");
        }
      }
    }

    return messages;
  };

  // Function to check if all required fields are filled
  // Function to check if all required fields are filled
  const isFormValid = () => {
    return getValidationMessages().length === 0;
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            variant="outline"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Button>
          <h1 className="text-2xl font-bold">{isEditProject ? 'Edit Project' : 'Create New Project'}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 relative">
          {isLoadingData && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-xl">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600">Loading project data...</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                
                Project Name <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
              <Input
                name="projectName"
                value={formData.projectName}
                required
                onChange={handleChange}
                placeholder="Enter project name"
                className="pl-9"
              />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Number <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
              <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
              <Input
                name="projectNumber"
                required
                value={formData.projectNumber}
                onChange={handleChange}
                placeholder="Enter project number"
                className="pl-9"
              />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="location">Location <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.location}
                  onValueChange={(value: string) => {
                      setFormData(prev => ({ ...prev, location: value }));
                  }}
                  placeholder="Select or enter location"
                  searchPlaceholder="Search locations..."
                  options={locations.map(loc => ({ id: loc.location_id, name: loc.location_name }))}
                  onAddNew={() => setIsAddingLocation(true)}
                  addNewText="Add New Location"
                  icon={<MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                  disabled={isLoadingData || loading}
                  loading={isLoadingData}
                  loadingText="Loading locations..."
                />
              </div>
            </div>

            <div>
              <Label htmlFor="customer">Customer <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.customer}
                  onValueChange={(value: string) => {
                      setFormData(prev => ({ ...prev, customer: value }));
                  }}
                  placeholder="Select or enter customer name"
                  searchPlaceholder="Search customers..."
                  options={customers.map(cust => ({ id: cust.company_id, name: cust.company_name }))}
                  onAddNew={() => setIsAddingCustomer(true)}
                  addNewText="Add New Customer"
                  icon={<UserRound className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                  disabled={isLoadingData || loading}
                  loading={isLoadingData}
                  loadingText="Loading customers..."
                />
              </div>
            </div>
           
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <AntdDate
                name="startDate"
                value={formData.startDate}
                onChange={(value: string) => {
                  setFormData(prev => ({ ...prev, startDate: value }));
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date 
                {/* <span className="text-red-500">*</span>
                {!formData.startDate && (
                  <span className="text-xs text-gray-500 ml-2">(Select start date first)</span>
                )} */}
              </label>
              <AntdDate
                name="endDate"
                value={formData.endDate}
                disabled={!formData.startDate}
                minDate={formData.startDate ? dayjs(formData.startDate) : undefined}
                onChange={(value: string) => {
                  // Validate that end date is after start date
                  if (formData.startDate && value) {
                    const startDate = new Date(formData.startDate);
                    const endDate = new Date(value);
                    
                    if (endDate <= startDate) {
                      customNotification.warning('End date must be after start date');
                      return;
                    }
                  }
                  setFormData(prev => ({ ...prev, endDate: value }));
                }}
              />
            </div>
            <div>
              <Label htmlFor="unit">Unit (Metric or Imperial) <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <Select
                  value={formData.unit || "in"}
                  onValueChange={(value: string) => {
                    setFormData(prev => ({ ...prev, unit: value }));
                  }}
                  disabled={isLoadingData || loading}
                >
                  <SelectTrigger className="pl-9">
                    <Scale className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="contractorName">Contractor Name</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.contractorName}
                  onValueChange={(value: string) => {
                      setFormData(prev => ({ ...prev, contractorName: value }));
                  }}
                  placeholder="Select or enter contractor name"
                  searchPlaceholder="Search contractors..."
                  options={contractors.map((contractor) => ({
                    id: contractor.contractor_id ?? contractor.contractor_name,
                    name: contractor.contractor_name,
                  }))}
                  onAddNew={() => setIsAddingContractor(true)}
                  addNewText="Add New Contractor"
                  icon={<HardHat className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                  disabled={isLoadingData || loading}
                  loading={isLoadingData}
                  loadingText="Loading contractors..."
                />
              </div>
            </div>
          </div>
          {/* Status Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Project Status <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-6">
              {STATUS_TYPES.map((status) => (
                <label key={status} className="flex items-center space-x-2">
                  <Checkbox
                    checked={formData.progress === status || (!formData.progress && status === 'Initiated')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData(prev => ({ ...prev, progress: status }));
                      }
                    }}
                    id={`status-${status}`}
                  />
                  <span className="text-sm font-medium text-gray-700">{status}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[#865e9c] mb-3">Type of Project <span className="text-red-500">*</span></h3>
            <div className="flex gap-6">
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes?.pipeline?.enabled || false}
                  onCheckedChange={handleCheckboxChange("projectTypes", "pipeline")}
                  id="pipeline"
                />
                <span className="text-sm font-medium text-gray-700">Pipeline</span>
              </label>
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes?.fabrication?.enabled || false}
                  onCheckedChange={handleCheckboxChange("projectTypes", "fabrication")}
                  id="fabrication"
                />
                <span className="text-sm font-medium text-gray-700">Fabrication</span>
              </label>
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes?.others?.enabled || false}
                  onCheckedChange={handleCheckboxChange("projectTypes", "others")}
                  id="others"
                />
                <span className="text-sm font-medium text-gray-700">Others</span>
              </label>
            </div>

            {/* Pipeline Features */}
            {formData.projectTypes?.pipeline?.enabled && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Pipeline Features <span className="text-red-500">*</span></h3>
                <div className="space-y-4">
                  {/* Material Traceability */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes?.pipeline?.features?.materialTraceability?.enabled || false}
                      onCheckedChange={handlePipelineFeatureChange('materialTraceability')}
                      id="pipeline-materialTraceability"
                    />
                    <Label htmlFor="pipeline-materialTraceability" className="text-sm">
                      Material Traceability
                    </Label>
                  </div>

                  {/* PipeFitter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes?.pipeline?.features?.pipeFitter?.enabled || false}
                      onCheckedChange={handlePipelineFeatureChange('pipeFitter')}
                      id="pipeline-pipeFitter"
                    />
                    <Label htmlFor="pipeline-pipeFitter" className="text-sm">
                      PipeFitter
                    </Label>
                  </div>

                  {/* Welding feature with machines */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={formData.projectTypes?.pipeline?.features?.welding?.enabled || false}
                        onCheckedChange={handlePipelineWeldingFeatureChange}
                        id="pipeline-welding"
                      />
                      <Label htmlFor="pipeline-welding" className="text-sm">
                        Welding
                      </Label>
                    </div>

                    {formData.projectTypes?.pipeline?.features?.welding?.enabled && (
                      <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={isAnyCrceWeldingMachineEnabled(formData.projectTypes?.pipeline?.features?.welding?.machines)}
                                onCheckedChange={(checked) => {
                                  CRCE_WELDING_MACHINE_KEYS.forEach((machine) => {
                                    handlePipelineWeldingMachineChange('CRCE', machine)(checked);
                                  });
                                }}
                                id="pipeline-crce"
                              />
                              <Label htmlFor="pipeline-crce" className="text-xs font-medium">CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-3 gap-2 pl-4">
                              {CRCE_WELDING_MACHINE_OPTIONS.map(({ key, display }) => (
                                <div key={key} className="flex items-center gap-2">
                                  <Checkbox
                                    checked={formData.projectTypes?.pipeline?.features?.welding?.machines?.[key]?.enabled || false}
                                    onCheckedChange={handlePipelineWeldingMachineChange('CRCE', key)}
                                    id={`pipeline-machine-${key}`}
                                  />
                                  <Label htmlFor={`pipeline-machine-${key}`} className="text-xs">{display}</Label>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Non CRCE Machines */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={formData.projectTypes?.pipeline?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                onCheckedChange={(checked) => {
                                  handlePipelineWeldingMachineChange('NonCRCE', 'competitorMachine')(checked);
                                }}
                                id="pipeline-noncrce"
                              />
                              <Label htmlFor="pipeline-noncrce" className="text-xs font-medium">Non-CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-4">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.pipeline?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                  onCheckedChange={handlePipelineWeldingMachineChange('NonCRCE', 'competitorMachine')}
                                  id="pipeline-machine-competitor"
                                />
                                <Label htmlFor="pipeline-machine-competitor" className="text-xs">Competitor Machine</Label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NDT */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes?.pipeline?.features?.ndt?.enabled || false}
                      onCheckedChange={handlePipelineFeatureChange('ndt')}
                      id="pipeline-ndt"
                    />
                    <Label htmlFor="pipeline-ndt" className="text-sm">
                      NDT
                    </Label>
                  </div>

                  {/* Coating */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.projectTypes?.pipeline?.features?.coating?.enabled || false}
                        onCheckedChange={handlePipelineFeatureChange('coating')}
                        id="pipeline-coating"
                      />
                      <Label htmlFor="pipeline-coating" className="text-sm">
                        Coating
                      </Label>
                    </div>
                    {formData.projectTypes?.pipeline?.features?.coating?.enabled && (
                      <div className="mt-2 pl-4 border-l border-[#427290]">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={formData.projectTypes?.pipeline?.features?.coating?.machines?.pumpHouse?.enabled || false}
                            onCheckedChange={handlePipelineCoatingPumpHouseChange}
                            id="pipeline-coating-pump-house"
                          />
                          <Label htmlFor="pipeline-coating-pump-house" className="text-xs">
                            Pump House
                          </Label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Fabrication Options */}
            {formData.projectTypes?.fabrication?.enabled && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Fabrication Options <span className="text-red-500">*</span></h3>
                <div className="flex gap-8">
                  {Object.entries({
                    buckleArrestor: 'Buckle Arrestor',
                    // pipeInPipe: 'Pipe in Pipe',
                    // bulkhead: 'Bulkhead',
                    spools: 'Spools'
                  }).map(([key, label]) => {
                    const optionKey = key as 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools';
                    return (
                      <div key={key} className="flex-1">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.selected || false}
                            onCheckedChange={handleFabricationOptionChange(optionKey)}
                            id={`fab-${key}`}
                          />
                          <Label htmlFor={`fab-${key}`} className="text-sm font-medium">
                            {label}
                          </Label>
                        </div>

                        {formData.projectTypes?.fabrication?.options?.[optionKey]?.selected && (
                          <div className="mt-2 pl-4 border-l border-[#427290] animate-in fade-in duration-500">
                            <div className="space-y-4">
                              {/* Material Traceability */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.materialTraceability?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'materialTraceability')}
                                  id={`fab-${optionKey}-materialTraceability`}
                                />
                                <Label htmlFor={`fab-${optionKey}-materialTraceability`} className="text-sm">
                                  Material Traceability
                                </Label>
                              </div>

                              {/* PipeFitter */}
                              <div  key={`fab-${optionKey}-pipeFitter`} className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.pipeFitter?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'pipeFitter')}
                                  id={`fab-${optionKey}-pipeFitter`}
                                />
                                <Label htmlFor={`fab-${optionKey}-pipeFitter`} className="text-sm">
                                  PipeFitter
                                </Label>
                              </div>

                              {/* Welding feature with machines */}
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Checkbox
                                    checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.enabled || false}
                                    onCheckedChange={handleWeldingFeatureChange(optionKey)}
                                    id={`fab-${optionKey}-welding`}
                                  />
                                  <Label htmlFor={`fab-${optionKey}-welding`} className="text-sm">
                                    Welding
                                  </Label>
                                </div>

                                {formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.enabled && (
                                  <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                                    <div className="space-y-4">
                                      <div>
                                        <div className="flex items-center gap-2 mb-2">
                                          <Checkbox
                                            checked={isAnyCrceWeldingMachineEnabled(formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.machines)}
                                            onCheckedChange={(checked) => {
                                              CRCE_WELDING_MACHINE_KEYS.forEach((machine) => {
                                                handleWeldingMachineChange(optionKey, 'CRCE', machine)(checked);
                                              });
                                            }}
                                            id={`fab-${optionKey}-crce`}
                                          />
                                          <Label htmlFor={`fab-${optionKey}-crce`} className="text-xs font-medium">CRCE Machines</Label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pl-4">
                                          {CRCE_WELDING_MACHINE_OPTIONS.map(({ key, display }) => (
                                            <div key={key} className="flex items-center gap-2">
                                              <Checkbox
                                                checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.machines?.[key]?.enabled || false}
                                                onCheckedChange={handleWeldingMachineChange(optionKey, 'CRCE', key)}
                                                id={`fab-${optionKey}-machine-${key}`}
                                              />
                                              <Label htmlFor={`fab-${optionKey}-machine-${key}`} className="text-xs">{display}</Label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Non CRCE Machines */}
                                      <div>
                                        <div className="flex items-center gap-2 mb-2">
                                          <Checkbox
                                            checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                            onCheckedChange={(checked) => {
                                              handleWeldingMachineChange(optionKey, 'NonCRCE', 'competitorMachine')(checked);
                                            }}
                                            id={`fab-${optionKey}-noncrce`}
                                          />
                                            <Label htmlFor={`fab-${optionKey}-noncrce`} className="text-xs font-medium">Non-CRCE Machines</Label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pl-4">
                                          <div className="flex items-center gap-2">
                                            <Checkbox
                                              checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                              onCheckedChange={handleWeldingMachineChange(optionKey, 'NonCRCE', 'competitorMachine')}
                                              id={`fab-${optionKey}-machine-competitor`}
                                            />
                                            <Label htmlFor={`fab-${optionKey}-machine-competitor`} className="text-xs">Competitor Machine</Label>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* NDT */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.ndt?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'ndt')}
                                  id={`fab-${optionKey}-ndt`}
                                />
                                <Label htmlFor={`fab-${optionKey}-ndt`} className="text-sm">
                                  NDT
                                </Label>
                              </div>

                              {/* Hydro */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.hydro?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'hydro')}
                                  id={`fab-${optionKey}-hydro`}
                                />
                                <Label htmlFor={`fab-${optionKey}-hydro`} className="text-sm">
                                  Hydro
                                </Label>
                              </div>

                              {/* Torquing */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.torquing?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'torquing')}
                                  id={`fab-${optionKey}-torquing`}
                                />
                                <Label htmlFor={`fab-${optionKey}-torquing`} className="text-sm">
                                  Torquing
                                </Label>
                              </div>

                              {/* Coating */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.fabrication?.options?.[optionKey]?.features?.coating?.enabled || false}
                                  onCheckedChange={handleFeatureChange(optionKey, 'coating')}
                                  id={`fab-${optionKey}-coating`}
                                />
                                <Label htmlFor={`fab-${optionKey}-coating`} className="text-sm">
                                  Coating
                                </Label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Others Features */}
            {formData.projectTypes?.others?.enabled && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Other Features <span className="text-red-500">*</span></h3>
                <div className="space-y-4">
                  {/* Material Traceability */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes.others.features?.materialTraceability?.enabled || false}
                      onCheckedChange={handleOthersFeatureChange('materialTraceability')}
                      id="others-materialTraceability"
                    />
                    <Label htmlFor="others-materialTraceability" className="text-sm">
                      Material Traceability
                    </Label>
                  </div>

                  {/* PipeFitter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes.others.features?.pipeFitter?.enabled || false}
                      onCheckedChange={handleOthersFeatureChange('pipeFitter')}
                      id="others-pipeFitter"
                    />
                    <Label htmlFor="others-pipeFitter" className="text-sm">
                      PipeFitter
                    </Label>
                  </div>

                  {/* Welding feature with machines */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={formData.projectTypes.others.features?.welding?.enabled || false}
                        onCheckedChange={handleOthersWeldingFeatureChange}
                        id="others-welding"
                      />
                      <Label htmlFor="others-welding" className="text-sm">
                        Welding
                      </Label>
                    </div>

                    {formData.projectTypes.others.features?.welding?.enabled && (
                      <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={isAnyCrceWeldingMachineEnabled(formData.projectTypes?.others?.features?.welding?.machines)}
                                onCheckedChange={(checked) => {
                                  CRCE_WELDING_MACHINE_KEYS.forEach((machine) => {
                                    handleOthersWeldingMachineChange('CRCE', machine)(checked);
                                  });
                                }}
                                id="others-crce"
                              />
                              <Label htmlFor="others-crce" className="text-xs font-medium">CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-4">
                              {CRCE_WELDING_MACHINE_OPTIONS.map(({ key, display }) => (
                                <div key={key} className="flex items-center gap-2">
                                  <Checkbox
                                    checked={formData.projectTypes?.others?.features?.welding?.machines?.[key]?.enabled || false}
                                    onCheckedChange={handleOthersWeldingMachineChange('CRCE', key)}
                                    id={`others-machine-${key}`}
                                  />
                                  <Label htmlFor={`others-machine-${key}`} className="text-xs">{display}</Label>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Non CRCE Machines */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={formData.projectTypes?.others?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                onCheckedChange={(checked) => {
                                  handleOthersWeldingMachineChange('NonCRCE', 'competitorMachine')(checked);
                                }}
                                id="others-noncrce"
                              />
                              <Label htmlFor="others-noncrce" className="text-xs font-medium">Non-CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-4">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.projectTypes?.others?.features?.welding?.machines?.competitorMachine?.enabled || false}
                                  onCheckedChange={handleOthersWeldingMachineChange('NonCRCE', 'competitorMachine')}
                                  id="others-machine-competitor"
                                />
                                <Label htmlFor="others-machine-competitor" className="text-xs">Competitor Machine</Label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NDT */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes.others.features?.ndt?.enabled || false}
                      onCheckedChange={handleOthersFeatureChange('ndt')}
                      id="others-ndt"
                    />
                    <Label htmlFor="others-ndt" className="text-sm">
                      NDT
                    </Label>
                  </div>

                  {/* Coating */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.projectTypes.others.features?.coating?.enabled || false}
                      onCheckedChange={handleOthersFeatureChange('coating')}
                      id="others-coating"
                    />
                    <Label htmlFor="others-coating" className="text-sm">
                      Coating
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            {!isFormValid() && !isLoading && !isLoadingData ? (
              <div className="relative group">
                                 <Button
                   disabled={true}
                   type="submit"
                   className="w-40 bg-gray-300 text-white py-2 rounded-lg cursor-not-allowed"
                 >
                  {isEditProject ? 'Update Project' : 'Submit Project'}
                </Button>
                                 {/* Tooltip */}
                 <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 min-w-[280px] max-w-[320px]">
                   <div className="w-full">
                     <div className="font-semibold mb-1">Please complete the following:</div>
                     <ul className="list-disc list-inside space-y-1 text-xs">
                       {getValidationMessages().map((message, index) => (
                         <li key={index} className="text-gray-200 break-words">{message}</li>
                       ))}
                     </ul>
                   </div>
                   {/* Arrow */}
                   <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                 </div>
              </div>
            ) : (
              <Button
                disabled={isLoading || isLoadingData || !isFormValid()}
                type="submit"
                   className="w-40 bg-[#EB1C37] text-white py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
               // className="w-40 text-white py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors  disabled:cursor-not-allowed"
                loading={isLoading}
                loadingText={isEditProject ? "Updating..." : "Submitting..."}
              >
                {isEditProject ? 'Update Project' : 'Submit Project'}
              </Button>
            )}
          </div>
        </form>

        {/* Add New Location Dialog */}
        <AddNewItemDialog
          open={isAddingLocation}
          onOpenChange={setIsAddingLocation}
          title="Add New Location"
          label="Location Name"
          placeholder="Enter location name"
          onAdd={handleAddLocation}
          buttonText="Add Location"
        />

        {/* Add New Customer Dialog */}
        <AddNewItemDialog
          open={isAddingCustomer}
          onOpenChange={setIsAddingCustomer}
          title="Add New Customer"
          label="Customer Name"
          placeholder="Enter customer name"
          onAdd={handleAddCustomer}
          buttonText="Add Customer"
        />

        <AddNewItemDialog
          open={isAddingContractor}
          onOpenChange={setIsAddingContractor}
          title="Add New Contractor"
          label="Contractor Name"
          placeholder="Enter contractor name"
          onAdd={handleAddContractor}
          buttonText="Add Contractor"
        />

        {/* Uncheck Warning Dialog */}
        {uncheckWarningData && (
          <UncheckWarningDialog
            open={showUncheckWarning}
            onOpenChange={setShowUncheckWarning}
            onConfirm={handleUncheckWarningConfirm}
            onCancel={handleUncheckWarningCancel}
            title={uncheckWarningData.title}
            message={uncheckWarningData.message}
            hasDocuments={uncheckWarningData.hasDocuments}
            hasPipes={uncheckWarningData.hasPipes}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectCreationForm; 